import React, { useEffect, useMemo, useState } from 'react';
import { NudosGenericModal, NudosModalContent } from '../../../../../../components/NudosComponents';
import { IorganizationBusinessData } from '../../../../../../types/bussines';
import {
  CONFIG_CLASS_ID,
  IConfigDataItemCreate,
  IOrganizationConfig,
  IOrganizationConfigCreate
} from '../../../../../../types/product.configuration.types';
import useConfigurationGlobalState from '../configurationGlobalState';
import { getConfigClass } from '../../../../../../utils/configurations';
import { addOrganizationConfig } from '../../../../../../services/product-configuration.service';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../utils/displayNudosStandardNotifications';
import AddConfigModalContent from './AddConfigModalContent';
import './AddConfigModal.scss';

const AddConfigModal = ({
  organizationData,
  closeModalCallback,
  mutateGetOrganizationConfigByOrganizationId
}: {
  organizationData: IorganizationBusinessData;
  closeModalCallback: () => void;
  mutateGetOrganizationConfigByOrganizationId: () => Promise<IOrganizationConfig[] | undefined>;
}) => {
  const { configUnit } = useConfigurationGlobalState();

  const [selectedConfigClassId, setSelectedConfigClassId] = useState<CONFIG_CLASS_ID>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState<Record<number, string | File>>({});
  const handleSetFormValues: React.Dispatch<React.SetStateAction<Record<number, string | File>>> = values => {
    setFormValues(values);
  };
  const [configurationNameField, setConfigurationNameField] = useState('');
  const [isFormValid, setIsFormValid] = useState(true);

  const selectedConfigClass = getConfigClass(selectedConfigClassId);
  const configClassIds = [
    CONFIG_CLASS_ID.DEP,
    CONFIG_CLASS_ID.MDM_ENROLLMENT,
    CONFIG_CLASS_ID.CREAR_ADMIN,
    CONFIG_CLASS_ID.ENCRIPTAR_BITLOCKER,
    CONFIG_CLASS_ID.HASH_ID,
    CONFIG_CLASS_ID.CONFIG_AVANZADA,
    CONFIG_CLASS_ID.APPLE_CONFIGURATOR,
    CONFIG_CLASS_ID.APPLE_CARE_PLUS,
    CONFIG_CLASS_ID.GARANTIA_1_ANO,
    CONFIG_CLASS_ID.GARANTIA_3_ANOS
  ];

  const isButtonDisabled = useMemo(() => {
    if (!selectedConfigClassId || isSubmitting || configurationNameField === '') {
      return true;
    }
    if (selectedConfigClass?.fields !== undefined) {
      return !isFormValid;
    }
    return false;
  }, [selectedConfigClassId, isSubmitting, selectedConfigClass?.fields, isFormValid, configurationNameField]);

  const handleConfigClassSelection = (classId: CONFIG_CLASS_ID) => {
    setSelectedConfigClassId(classId);
    setFormValues({});
    setConfigurationNameField('');
    setIsFormValid(true);
  };

  const createOrganizationConfig = async () => {
    const configData: IConfigDataItemCreate[] = [];
    let fileToUpload: File | null = null;
    Object.entries(formValues).forEach(([fieldId, value]) => {
      if (typeof value === 'string') {
        configData.push({
          fieldNameId: parseInt(fieldId),
          fieldValue: value
        });
      } else if (value instanceof File) {
        fileToUpload = value;
      }
    });
    const createOrganizationConfigData: IOrganizationConfigCreate = {
      organizationId: organizationData?.id,
      configClassId: selectedConfigClassId!,
      name: configurationNameField,
      configData
    };
    if (fileToUpload) {
      createOrganizationConfigData.file = fileToUpload;
    }

    return await addOrganizationConfig(createOrganizationConfigData);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await createOrganizationConfig();

      displaySuccessNotification({
        customJSXMessage: <>Configuración agregada con éxito</>,
        duration: 3000
      });
      mutateGetOrganizationConfigByOrganizationId();
    } catch {
      displayErrorNotification();
    } finally {
      closeModalCallback();
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (configUnit?.configData) {
      const autoFillValues: Record<number, string | File> = {};
      configUnit.configData.forEach(dataItem => {
        autoFillValues[dataItem.fieldName.id] = dataItem.fieldValue;
      });
      setFormValues(autoFillValues);
    }
  }, [configUnit?.configData]);

  return (
    <NudosGenericModal
      customClassName="addConfigModal"
      closeModalCallback={closeModalCallback}
      modalContent={
        <NudosModalContent
          customWidth="580px"
          customHeight="470px"
          modalContentTitle="Agregar configuraciones"
          hideNudosIcon
          toCloseModal={closeModalCallback}
          CustomModalComponent={
            <AddConfigModalContent
              organizationData={organizationData}
              configClassIds={configClassIds}
              selectedConfigClassId={selectedConfigClassId as CONFIG_CLASS_ID}
              handleConfigClassSelection={handleConfigClassSelection}
              configurationNameField={configurationNameField}
              setConfigurationNameField={setConfigurationNameField}
              selectedConfigClass={selectedConfigClass}
              formValues={formValues}
              setFormValues={handleSetFormValues}
              setIsFormValid={setIsFormValid}
              handleSubmit={handleSubmit}
              isButtonDisabled={isButtonDisabled}
            />
          }
        />
      }
    />
  );
};

export default AddConfigModal;
